// ============================================================
//  useRecentAuth — ADMIN DASHBOARD
//  Path: src/hooks/useRecentAuth.tsx
//
//  Several backend routes refuse a request unless the admin signed in
//  within the last few minutes — requireRecentAuth(5, ...) in
//  admin.routes.js: create / remove admin, change an admin's role,
//  reset a password, delete a user, delete a company. Nothing in the
//  dashboard ever re-verified the admin before calling them, so after
//  the first 5 minutes of a session those actions simply failed (some
//  silently). And because config/api.ts signs the admin out on ANY
//  401, a refusal returned as 401 would log them out mid-task.
//
//  This hook checks BEFORE the request instead of reacting after it:
//
//    const { runWithRecentAuth, reauthModal } = useRecentAuth()
//    ...
//    runWithRecentAuth(async () => { await removeAdmin(id) })
//    ...
//    return <>{...page...}{reauthModal}</>
//
//  If the sign-in time on the admin's current ID token is recent
//  enough, the action runs immediately. Otherwise a modal asks for the
//  password (and, for an account with an authenticator app enrolled,
//  the 6-digit code), re-authenticates with Firebase, forces a fresh
//  ID token so the backend sees the new sign-in time, then runs the
//  action. The action keeps its own try/catch and error display — this
//  hook only decides whether a re-check is needed first.
// ============================================================

import React, { useRef, useState } from 'react'
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  MultiFactorError,
} from 'firebase/auth'
import { auth } from '../config/firebase'
import { useAuth } from '../context/AuthContext'

// The backend allows 5 minutes (requireRecentAuth(5, ...)). Re-verify
// after 4 so a small clock difference between this browser and the
// server can't let a request through here that the server then refuses.
const RECENT_AUTH_WINDOW_MS = 4 * 60 * 1000

const signedInRecently = async (): Promise<boolean> => {
  const user = auth.currentUser
  if (!user) return false
  try {
    // authTime = when this user last actually entered credentials
    // (sign-in or re-authentication), NOT when the token was refreshed.
    const { authTime } = await user.getIdTokenResult()
    const signedInAt = Date.parse(authTime)
    return Number.isFinite(signedInAt) && Date.now() - signedInAt < RECENT_AUTH_WINDOW_MS
  } catch {
    return false
  }
}

export const useRecentAuth = () => {
  const { mfaEnrolled } = useAuth()

  const [open, setOpen]           = useState(false)
  const [password, setPassword]   = useState('')
  const [code, setCode]           = useState('')
  const [needsCode, setNeedsCode] = useState(false)
  const [error, setError]         = useState('')
  const [busy, setBusy]           = useState(false)
  // The action waiting for a successful re-check. A ref, not state:
  // it's never rendered, and it must survive re-renders unchanged.
  const pendingAction = useRef<(() => Promise<void>) | null>(null)

  const reset = () => {
    setPassword('')
    setCode('')
    setError('')
    setBusy(false)
  }

  const cancel = () => {
    pendingAction.current = null
    setOpen(false)
    reset()
  }

  // Runs `action` now if the sign-in is recent enough, otherwise after
  // the admin re-verifies. Cancelling the modal drops the action.
  const runWithRecentAuth = async (action: () => Promise<void>) => {
    if (await signedInRecently()) {
      await action()
      return
    }
    pendingAction.current = action
    reset()
    setNeedsCode(mfaEnrolled)
    setOpen(true)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const user = auth.currentUser
    if (!user?.email) {
      setError('Not signed in.')
      return
    }
    if (needsCode && code.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app.')
      return
    }

    setBusy(true)
    setError('')
    try {
      try {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password))
      } catch (err: any) {
        if (err?.code !== 'auth/multi-factor-auth-required') throw err
        // Password was correct; Firebase wants the second factor too.
        const resolver = getMultiFactorResolver(auth, err as MultiFactorError)
        const totpHint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID)
        if (!totpHint) {
          setError('This account has no authenticator app enrolled. Sign out and sign in again instead.')
          return
        }
        if (code.length !== 6) {
          // Account has MFA but the code field wasn't shown yet
          // (mfaEnrolled was stale) — show it and ask again.
          setNeedsCode(true)
          setError('Enter the 6-digit code from your authenticator app.')
          return
        }
        await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim()))
      }

      // Make sure the next API request carries a token with the NEW
      // sign-in time — that's what the backend's recent-auth check reads.
      await user.getIdToken(true)

      const action = pendingAction.current
      pendingAction.current = null
      setOpen(false)
      reset()
      if (action) await action()
    } catch (err: any) {
      const errCode = err?.code
      if (errCode === 'auth/wrong-password' || errCode === 'auth/invalid-credential') {
        setError('Incorrect password.')
      } else if (errCode === 'auth/invalid-verification-code') {
        setError('That code is incorrect or has expired — check the time on your device and try the next code.')
        setCode('')
      } else if (errCode === 'auth/too-many-requests') {
        setError('Too many attempts — please wait a few minutes and try again.')
      } else if (errCode === 'auth/network-request-failed') {
        setError('Could not reach the server — check your connection and try again.')
      } else {
        setError('Could not verify your identity — try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  const reauthModal = open ? (
    // z-[60]: must sit above the page's own modals (z-50), since an
    // action inside one of them (e.g. Reset Password) can trigger this.
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-sm">
        <h3 className="text-lg font-semibold text-white mb-2">Confirm it's you</h3>
        <p className="text-sm text-gray-400 mb-4">
          This action needs a recent sign-in. Enter your password{needsCode ? ' and the code from your authenticator app' : ''} to continue.
        </p>
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
        <form onSubmit={submit}>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" required autoFocus autoComplete="current-password"
            className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-3" />
          {needsCode && (
            <input type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code" maxLength={6}
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white text-center tracking-[0.4em] placeholder-gray-600 focus:border-brand-500 focus:outline-none mb-3" />
          )}
          <div className="flex gap-3 justify-end mt-1">
            <button type="button" onClick={cancel}
              className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={busy || !password || (needsCode && code.length !== 6)}
              className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors">
              {busy ? 'Verifying...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : null

  return { runWithRecentAuth, reauthModal }
}